import { Auth } from 'aws-amplify';
import React, { useEffect, useState } from 'react';
import { SocketClient, SocketClientListeners } from './SocketClient';
import { DqSocketContextData, DqSocketRequestOptions } from './types';

export interface DqSocketProviderProps {
  Context: React.Context<DqSocketContextData>;
  client: SocketClient;
  token?: string;
}

export const DqSocketProvider: React.FC<React.PropsWithChildren<DqSocketProviderProps>> = ({ Context, ...props }) => {
  const client = React.useRef(props.client);
  const requestCache = React.useRef(new Map<string, Promise<any>>());
  const [connected, setConnected] = useState(false);

  const listeners = React.useMemo(() => {
    const l: SocketClientListeners = {
      onConnect: () => {
        setConnected(client.current.socket.connected);
        console.log('socket connected');
      },
      onDisconnect: () => {
        setConnected(client.current.socket.connected);
      },
      onError: (err: any) => {
        setConnected(client.current.socket.connected);
        if (err && err.toLowerCase && err.toLowerCase().includes('auth')) {
          console.warn('Socket Auth failed', err);
          Auth.currentSession();
        }
      },
      onReconnect: (data: any) => {
        setConnected(client.current.socket.connected);
      },
      onReconnectFailed: (error: any) => {
        setConnected(client.current.socket.connected);
        console.log('socket reconnect_failed', error);
      },
    };
    return l;
  }, []);

  useEffect(() => {
    client.current.setListeners(listeners);
    setConnected(client.current.socket.connected);
  }, [listeners]);

  useEffect(() => {
    client.current.updateAuth(props.token);
    setConnected(client.current.socket.connected);
  }, [props.token]);

  // An effect that clears authentication (and subsequently disconnects the socket) when the component is unmounted.
  useEffect(() => {
    const socket = client.current;

    return () => {
      socket?.updateAuth(undefined);
    };
  }, []);

  const request = React.useCallback(
    async <R extends unknown>(event: string, data: any, options?: DqSocketRequestOptions) => {
      if (connected !== client.current.socket.connected) {
        setConnected(client.current.socket.connected);
      }
      if (!client.current.socket.connected) throw new Error(`Not attempting request, socket not connected! ${event}`);
      const cacheKey = JSON.stringify({ event, data });
      if (requestCache.current.has(cacheKey))
        throw new Error('Identical request in-flight, not attempting: ' + cacheKey);

      const request = client.current.request<R>(event, data);
      requestCache.current.set(cacheKey, request);
      try {
        options?.signal?.throwIfAborted();
        const result = await request;
        options?.signal?.throwIfAborted();
        return result;
      } finally {
        requestCache.current.delete(cacheKey);
      }
    },
    [connected],
  );

  const ctxValue: DqSocketContextData = React.useMemo(
    () => ({ socket: client.current.socket, request: request, connected }),
    [connected, request],
  );

  return <Context.Provider value={ctxValue}>{props.children}</Context.Provider>;
};
